/**
 * NotificationsCard — the Dashboard's "Alerts" card.
 *
 * Named for the data it shows (the per-user `notifications` the Alerts page
 * lists). It is unrelated to AlertsBanner / AlertHeroCard in
 * components/dashboard/, which show practice-wide announcements.
 *
 * Copies the chrome of the Dashboard's Unread Messages and Pending Tasks cards
 * (DashboardPage.tsx): title, count pill, View All, up to 7 rows, one footer
 * action. The pill reads the shared unread count, the same number as the
 * sidebar's Alerts badge; rows are the newest unread alerts.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { getNotifications, markAllRead, markRead, type Notification } from '@/services/notifications.service';
import { notificationTitle, openNotification } from '@/lib/notification-display';
import { NOTIFICATIONS_KEY, useUnreadAlertCount } from '@/hooks/useUnreadAlertCount';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { relativeTime } from '@/pages/inbox/inboxShared';
import { toast } from '@/store/toastStore';

const ROW_LIMIT = 7;

export function NotificationsCard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const visitPatient = usePatientHistoryStore((s) => s.visit);
  const [markingAll, setMarkingAll] = useState(false);

  const { data: unreadCount = 0 } = useUnreadAlertCount();
  const { data: rowsPage, isPending, isError } = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'dashboard-unread'],
    queryFn: () => getNotifications(1, ROW_LIMIT, true),
    staleTime: 60_000,
    retry: false,
  });
  const rows = rowsPage?.data ?? [];

  const refreshAlerts = () => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };

  function openRow(n: Notification) {
    // The target opens at once; the count refreshes once the write has landed.
    void openNotification(n, { navigate, visitPatient, markRead })
      .catch(() => undefined)
      .finally(refreshAlerts);
  }

  async function handleMarkAllRead() {
    setMarkingAll(true);
    try {
      await markAllRead();
      refreshAlerts();
    } catch {
      toast.error("Alerts couldn't be marked as read. Try again. If this keeps happening, contact your system administrator.");
    } finally {
      setMarkingAll(false);
    }
  }

  return (
    <Card className="border-border/80 shadow-sm h-full">
      <CardHeader className="pb-3 pt-4 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Alerts</span>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center h-5 px-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[20px]">
                {unreadCount}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" className="text-xs text-primary h-7 px-2 gap-1" onClick={() => navigate('/alerts')}>
            View All <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardBody className="px-0 pb-0">
        <div className="divide-y divide-border">
          {isPending ? (
            // Blank at the empty state's height, so the card does not jump or
            // flash "no alerts" beside a count that is already known.
            <div className="px-5 py-8" aria-busy="true">
              <div className="h-5" />
            </div>
          ) : isError ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">
              Alerts couldn't be loaded. Refresh the page. If this keeps happening, contact your system administrator.
            </p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">You have no unread alerts.</p>
          ) : (
            rows.slice(0, ROW_LIMIT).map((n) => (
              <button
                key={n.notificationId}
                type="button"
                onClick={() => openRow(n)}
                className={cn(
                  'flex w-full items-start gap-3 px-5 py-3 text-left transition-colors border-l-[3px]',
                  'border-l-red-500 bg-red-50/30 hover:bg-red-50/60',
                )}
              >
                <span className="mt-1.5 block h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-500" aria-hidden="true" />
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-medium text-primary truncate">
                    {n.display?.title || notificationTitle(n)}
                  </span>
                  {(n.display?.body || n.message) && (
                    <span className="block text-sm font-semibold text-slate-700 leading-snug line-clamp-2">
                      {n.display?.body || n.message}
                    </span>
                  )}
                </span>
                <span className="flex-shrink-0 text-[11px] text-muted-foreground">{relativeTime(n.createDate)}</span>
              </button>
            ))
          )}
        </div>
        <div className="px-5 py-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-primary gap-1 h-8"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0 || markingAll}
          >
            <CheckCheck className="h-3 w-3" /> Mark all as read
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
